/**
 * InteractiveMapSection — Weidner Classic
 *
 * Forked from the rise base so Building View can prefer an Engrain
 * Sightmap when the property has one. The rise section always mounts
 * `apartment-sync-map`. Weidner properties sometimes carry a custom
 * Engrain id instead (or as well). When any of these fields is set, the
 * Engrain embed is used:
 *
 *   - custom_engrain_sightmap_id
 *   - custom_engrain_sitemap_id
 *   - custom_engrain_sightmap_url
 *   - custom_engrain_sitemap_url
 *
 * Otherwise the configured map block (ApartmentSync by default) renders
 * as before. An editor pick of a block other than those two is left
 * alone.
 *
 * Maintenance checklist when re-syncing with the rise parent:
 *   1. Diff this file against the rise InteractiveMapSection.
 *   2. Keep the Engrain preference ahead of apartment-sync-map.
 *   3. Keep the same slot and `interactivemapsection.map_block` token.
 *   4. Run `pnpm run lint:schema` and `pnpm run typecheck`.
 */

import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  useProperty,
  useRegistry,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';

const ENGRAIN_SITEMAP_FIELDS = [
  'custom_engrain_sightmap_id',
  'custom_engrain_sitemap_id',
  'custom_engrain_sightmap_url',
  'custom_engrain_sitemap_url',
] as const;

function customText(property: Record<string, unknown> | null | undefined, key: string): string {
  const value = property?.[key];
  return typeof value === 'string' ? value.trim() : '';
}

function hasEngrainSitemap(property: Record<string, unknown> | null | undefined): boolean {
  return ENGRAIN_SITEMAP_FIELDS.some((key) => customText(property, key).length > 0);
}

export function InteractiveMapSection() {
  const property = useProperty() as unknown as Record<string, unknown>;
  const hasEngrain = hasEngrainSitemap(property);
  const registry = useRegistry();
  const mapSlot = useBlockSlot('interactivemapsection.map_block', {
    defaultValue: hasEngrain ? 'engrain-sitemap' : 'apartment-sync-map',
  });

  // A stored ApartmentSync selection is the inherited default, not an
  // explicit "keep ApartmentSync even when Engrain is configured" choice.
  // Swap it for the Engrain block so the sightmap id is what the page uses.
  const engrainComponent = registry['block:engrain-sitemap'];
  const replaceApartmentSync =
    hasEngrain &&
    !!engrainComponent &&
    (mapSlot == null || mapSlot.blockId === 'apartment-sync-map');

  const BlockComponent = replaceApartmentSync ? engrainComponent : mapSlot?.BlockComponent;
  const sectionId = mapSlot?.sectionId ?? 'interactivemapsection.map';

  return (
    <EditableToken
      path="interactivemapsection.map_block"
      as="div"
      className="min-h-[400px] md:min-h-[500px] flex flex-col bg-[var(--vibe-color-background)]"
    >
      {BlockComponent ? (
        <SectionContextProvider sectionId={sectionId}>
          <BlockComponent sectionId={sectionId} />
        </SectionContextProvider>
      ) : (
        <div>No block selected</div>
      )}
    </EditableToken>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InteractiveMapSection',
  displayName: 'Interactive Map Section',
  tokens: {
    'interactivemapsection.map_block': {
      label: 'Map Block',
      type: 'block',
      defaultValue: 'apartment-sync-map',
      acceptedBlockCategories: ['Integrations', 'Embeds'],
      description:
        'Interactive map widget. When the property has a custom Engrain sitemap id (custom_engrain_sightmap_id or custom_engrain_sitemap_id) or embed URL, the Engrain Sightmap is used instead of ApartmentSync.',
    },
  },
  slotGroups: {},
  collectionBindings: {},
};

(InteractiveMapSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default InteractiveMapSection;
